import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Physicalartwork } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { getMediaData, playbackUrl } from '@/lib/utils/media.utils';
import { processTagArray, getYearTag, truncateToWords, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Transformer for converting Physicalartwork entities to CatalogItem
 */
export class PhysicalTransformer extends BaseCatalogItemTransformer<Physicalartwork> {
  protected getId(physical: Physicalartwork): string | number {
    return physical.id;
  }

  protected getType(_physical: Physicalartwork): CatalogType {
    return 'physical';
  }

  protected getSlug(physical: Physicalartwork): string {
    return physical.slug;
  }

  protected getHref(physical: Physicalartwork): string {
    return `/catalog/physicals/${physical.slug}`;
  }

  protected getTitle(physical: Physicalartwork): string {
    return physical.title;
  }

  protected getDisplayTitle(physical: Physicalartwork): string {
    return physical.title;
  }

  protected getFilename(physical: Physicalartwork): string {
    return physical.slug;
  }

  protected getThumbnail(physical: Physicalartwork): string {
    const cardImageData = getMediaData(physical.cardImage);
    return cardImageData.url || '';
  }

  protected getImageUrl(physical: Physicalartwork): string {
    const cardImageData = getMediaData(physical.cardImage);
    const coverImageData = physical.coverImage ? getMediaData(physical.coverImage) : null;
    const thumbnail = cardImageData.url || '';
    return coverImageData?.url || thumbnail;
  }

  protected getWidth(physical: Physicalartwork): number {
    const cardImageData = getMediaData(physical.cardImage);
    return cardImageData.width || 800;
  }

  protected getHeight(physical: Physicalartwork): number {
    const cardImageData = getMediaData(physical.cardImage);
    return cardImageData.height || 600;
  }

  protected getDate(physical: Physicalartwork): string {
    return physical.updatedAt;
  }

  protected getYear(physical: Physicalartwork): number {
    return physical.year || new Date(physical.updatedAt).getFullYear();
  }

  protected getDescription(physical: Physicalartwork): string {
    return physical.description;
  }

  protected getLongDescription(physical: Physicalartwork): string {
    return physical.description;
  }

  protected getMedium(physical: Physicalartwork): string | undefined {
    return physical.medium || undefined;
  }

  protected getDimensions(physical: Physicalartwork): string | undefined {
    return physical.dimensions || undefined;
  }

  protected getLocation(physical: Physicalartwork): string | undefined {
    return physical.location || undefined;
  }

  protected getVideoUrl(physical: Physicalartwork): string | undefined {
    return playbackUrl(physical.video) || undefined;
  }

  protected getTags(physical: Physicalartwork): string[] {
    return combineTags(
      processTagArray(physical.tags),
      getYearTag(physical.updatedAt),
      truncateToWords(physical.medium, 3)
    );
  }
}

// Export singleton instance
export const physicalTransformer = new PhysicalTransformer();